/**
 * Utility functions for managing project roles lookup maps.
 *
 * This module provides functions to build and use lookup maps for project roles
 * to avoid N+1 queries. Instead of fetching project_role for each task/comment
 * individually, we fetch all memberships with project_role in the project query
 * and use a lookup map to associate roles with users.
 */

/**
 * Builds a lookup map from project memberships.
 *
 * @param {Array} memberships - Array of membership objects with { user: { id }, projectRole }
 * @returns {Map<string, string|null>} - Map of userId -> projectRole
 */
export function buildProjectRolesMap(memberships) {
  const map = new Map()

  if (!memberships || !Array.isArray(memberships)) {
    return map
  }

  for (const membership of memberships) {
    if (membership?.user?.id) {
      const userId = String(membership.user.id)
      map.set(userId, membership.projectRole || null)
    }
  }

  return map
}

/**
 * Gets the project role for a user in a project.
 *
 * @param {Map<string, string|null>} rolesMap - The lookup map from buildProjectRolesMap
 * @param {string|number} userId - The user ID
 * @returns {string|null} - The project role, or null if not found
 */
export function getProjectRole(rolesMap, userId) {
  if (!rolesMap || !userId) {
    return null
  }

  const userIdStr = String(userId)
  return rolesMap.get(userIdStr) || null
}
